import { isLegalKind } from '@shared/lib/documentKind';
import { ScopeBanner, ScopeErrorBanner, ScopeSuccessNote } from './styles';
import type { ContractVersionFile } from './types';

interface ScopeNoticeProps {
  items: ContractVersionFile[];
}

export function ScopeNotice({ items }: ScopeNoticeProps) {
  const legal = items.filter((f) => !f.no_analysis && isLegalKind(f.document_kind));
  const pending = items.filter(
    (f) => !f.no_analysis && (f.kind_source ?? 'pending') === 'pending',
  );

  const analyzableContract = items.some(
    (f) =>
      !f.no_analysis &&
      f.document_kind === 'contract' &&
      (f.content_chars ?? 0) > 0,
  );

  const attachedCount = items.filter((f) => f.no_analysis).length;
  const analyzed = items.filter((f) => !f.no_analysis);
  const empty = analyzed.filter((f) => (f.content_chars ?? 0) === 0);
  const attachedNote =
    attachedCount > 0 ? ` Ещё ${attachedCount} приложено без анализа.` : '';

  const totalChars = analyzed.reduce(
    (sum, f) => sum + (f.content_chars ?? 0),
    0,
  );

  const scopeBanner = (() => {
    if (pending.length === items.length) {
      return null;
    }
    if (!analyzableContract) {
      return (
        <ScopeBanner role="status">
          Среди файлов комплекта <strong>не распознан договор</strong> (или из него
          не извлёкся текст) — в анализ уйдёт весь комплект целиком ({items.length} файл(ов)).
          Это дороже и менее точно. Проверьте типы документов ниже и поправьте, если классификация ошиблась.
        </ScopeBanner>
      );
    }
    return (
      <ScopeBanner role="status">
        В юридический анализ идёт <strong>{legal.length}</strong> из{' '}
        {items.length} файл(ов) — договор, допсоглашения, протоколы разногласий
        и реквизиты. Остальные (сметы, акты, платёжки, чертежи) сохранены и
        доступны для скачивания, но в анализ не подаются: они не несут
        юридических условий и только удорожают разбор.
        {pending.length > 0 && <> Ещё {pending.length} файл(ов) в обработке.</>}
      </ScopeBanner>
    );
  })();

  const emptyNotice = (() => {
    if (empty.length > 0) {
      return (
        <ScopeErrorBanner role="alert">
          ⚠ Не распарсилось: <strong>{empty.length}</strong> из {analyzed.length}{' '}
          файл(ов) для анализа. LLM их <strong>не видела</strong>. Обычно это
          отсканированные PDF без OCR или зашифрованные документы — выделены
          жёлтым ниже.{attachedNote}
        </ScopeErrorBanner>
      );
    }
    return (
      <ScopeSuccessNote>
        ✓ Все {analyzed.length} файл(ов) проанализированы LLM (
        {totalChars.toLocaleString('ru-RU')} символов текста суммарно).
        {attachedNote}
      </ScopeSuccessNote>
    );
  })();

  return (
    <>
      {scopeBanner}
      {emptyNotice}
    </>
  );
}
